import { useEffect, useRef } from "react";
import { useLocation, useMatches } from "react-router-dom";
import {
  deriveModule,
  deriveViewLocation,
  isUntrackedRoute,
  pageView,
  rewriteUrlForMetrics,
  setRouteContext,
} from "@/lib/metrics";

/**
 * Keeps the metrics module's route context current and fires `pageview` on
 * route change. Renders nothing; mount once inside the router (RootLayout).
 * Keyed on pathname only, so query-param-only changes (e.g. `?mode=refine`)
 * don't fire duplicate pageviews.
 */
export function MetricsPageviews(): null {
  const location = useLocation();
  const matches = useMatches();
  const lastPathname = useRef<string | null>(null);

  useEffect(() => {
    // Skipped before the context is written, not just before the event: the
    // context carries `currentURL` too, and a later event would ship it.
    if (isUntrackedRoute(location.pathname)) return;

    const params = matches[matches.length - 1]?.params ?? {};
    setRouteContext({
      product: "Midship",
      module: deriveModule(location.pathname),
      viewLocation: deriveViewLocation(location.pathname, params),
      currentURL: rewriteUrlForMetrics(
        location.pathname,
        location.search,
        params,
      ),
    });

    // Context above tracks every location change so later events carry the
    // current URL; the pageview itself fires on pathname changes only.
    if (lastPathname.current === location.pathname) return;
    lastPathname.current = location.pathname;
    pageView();
  }, [location.pathname, location.search, matches]);

  return null;
}
